import { create } from 'zustand'
import projectService from '@services/projectService'

/**
 * Project Wizard Store
 *
 * Manages state for the multi-step project onboarding wizard
 */

const initialBasicDetails = {
	name: '',
	description: '',
	certificates: [],
}

const initialProjectScope = {
	industry: '',
	domain: '',
	technologyStack: [],
	geographicalLocations: [],
	teamRoles: [], // Legacy - kept for backward compatibility
	associatedRoles: [], // Array of role IDs: [3, 5, 11]
	startDate: '',
	targetDate: '',
}

const initialState = {
	// Current step (1-5)
	currentStep: 1,

	// Step 1: Basic Details
	basicDetails: { ...initialBasicDetails },

	// Step 2: Project Scope
	projectScope: { ...initialProjectScope },

	// Checklist type: 'ai' (default) | 'manual'
	checklistType: 'ai',

	// Step 3: Documents
	documents: [],
	uploadProgress: {},

	// Step 4: Review (uses basicDetails, projectScope, documents)

	// Step 5: Checklist
	checklist: [],
	isGenerating: false,
	generationError: null,

	// Created project data
	createdProject: null,

	// Overall wizard state
	isSubmitting: false,
	error: null,
}

export const useProjectWizardStore = create((set, get) => ({
	...initialState,

	// Navigation
	setStep: (step) => set({ currentStep: step }),

	nextStep: () => {
		const { currentStep } = get()
		if (currentStep < 5) {
			set({ currentStep: currentStep + 1 })
		}
	},

	prevStep: () => {
		const { currentStep } = get()
		if (currentStep > 1) {
			set({ currentStep: currentStep - 1 })
		}
	},

	goToStep: (step) => {
		if (step >= 1 && step <= 5) {
			set({ currentStep: step })
		}
	},

	// Step 1: Basic Details
	setBasicDetails: (details) =>
		set((state) => ({
			basicDetails: { ...state.basicDetails, ...details },
		})),

	setProjectName: (name) =>
		set((state) => ({
			basicDetails: { ...state.basicDetails, name },
		})),

	setProjectDescription: (description) =>
		set((state) => ({
			basicDetails: { ...state.basicDetails, description },
		})),

	setCertificates: (certificates) =>
		set((state) => ({
			basicDetails: { ...state.basicDetails, certificates },
		})),

	// Checklist type
	setChecklistType: (checklistType) => set({ checklistType }),

	// Step 2: Project Scope
	setProjectScope: (scope) =>
		set((state) => ({
			projectScope: { ...state.projectScope, ...scope },
		})),

	setIndustry: (industry) =>
		set((state) => ({
			projectScope: { ...state.projectScope, industry },
		})),

	setDomain: (domain) =>
		set((state) => ({
			projectScope: { ...state.projectScope, domain },
		})),

	setTechnologyStack: (technologyStack) =>
		set((state) => ({
			projectScope: { ...state.projectScope, technologyStack },
		})),

	addTechnology: (tech) =>
		set((state) => ({
			projectScope: {
				...state.projectScope,
				technologyStack: [...state.projectScope.technologyStack, tech],
			},
		})),

	removeTechnology: (tech) =>
		set((state) => ({
			projectScope: {
				...state.projectScope,
				technologyStack: state.projectScope.technologyStack.filter((t) => t !== tech),
			},
		})),

	setGeographicalLocations: (geographicalLocations) =>
		set((state) => ({
			projectScope: { ...state.projectScope, geographicalLocations },
		})),

	addLocation: (location) =>
		set((state) => ({
			projectScope: {
				...state.projectScope,
				geographicalLocations: [...state.projectScope.geographicalLocations, location],
			},
		})),

	removeLocation: (location) =>
		set((state) => ({
			projectScope: {
				...state.projectScope,
				geographicalLocations: state.projectScope.geographicalLocations.filter(
					(l) => l !== location
				),
			},
		})),

	setTeamRoles: (teamRoles) =>
		set((state) => ({
			projectScope: { ...state.projectScope, teamRoles },
		})),

	addTeamRole: (roleId) =>
		set((state) => ({
			projectScope: {
				...state.projectScope,
				teamRoles: [...state.projectScope.teamRoles, roleId],
			},
		})),

	removeTeamRole: (roleId) =>
		set((state) => ({
			projectScope: {
				...state.projectScope,
				teamRoles: state.projectScope.teamRoles.filter((r) => r !== roleId),
			},
		})),

	// Associated Roles (array of role IDs)
	setAssociatedRoles: (roleIds) =>
		set((state) => ({
			projectScope: { ...state.projectScope, associatedRoles: roleIds },
		})),

	addAssociatedRole: (roleId) =>
		set((state) => ({
			projectScope: {
				...state.projectScope,
				associatedRoles: [...state.projectScope.associatedRoles, roleId],
			},
		})),

	removeAssociatedRole: (roleId) =>
		set((state) => ({
			projectScope: {
				...state.projectScope,
				associatedRoles: state.projectScope.associatedRoles.filter((id) => id !== roleId),
			},
		})),

	setStartDate: (startDate) =>
		set((state) => ({
			projectScope: { ...state.projectScope, startDate },
		})),

	setTargetDate: (targetDate) =>
		set((state) => ({
			projectScope: { ...state.projectScope, targetDate },
		})),

	// Step 3: Documents
	addDocument: (document) =>
		set((state) => ({
			documents: [...state.documents, document],
		})),

	removeDocument: (documentId) =>
		set((state) => ({
			documents: state.documents.filter((d) => d.id !== documentId),
		})),

	updateDocument: (documentId, updates) =>
		set((state) => ({
			documents: state.documents.map((d) =>
				d.id === documentId ? { ...d, ...updates } : d
			),
		})),

	setUploadProgress: (documentId, progress) =>
		set((state) => ({
			uploadProgress: { ...state.uploadProgress, [documentId]: progress },
		})),

	clearUploadProgress: (documentId) =>
		set((state) => {
			const newProgress = { ...state.uploadProgress }
			delete newProgress[documentId]
			return { uploadProgress: newProgress }
		}),

	// Step 5: Checklist
	setChecklist: (checklist) => {
		// Normalize checklist items - parse JSON strings for tags
		const normalizedChecklist = checklist.map((item) => ({
			...item,
			tags: typeof item.tags === 'string' ? JSON.parse(item.tags || '[]') : (item.tags || []),
		}))
		set({ checklist: normalizedChecklist })
	},

	updateChecklistItem: (index, updates) =>
		set((state) => ({
			checklist: state.checklist.map((item, i) =>
				i === index ? { ...item, ...updates } : item
			),
		})),

	addChecklistItem: (item) =>
		set((state) => ({
			checklist: [
				...state.checklist,
				{
					...item,
					id: `temp_${Date.now()}`,
					sort_order: state.checklist.length,
				},
			],
		})),

	removeChecklistItem: (index) =>
		set((state) => ({
			checklist: state.checklist.filter((_, i) => i !== index),
		})),

	reorderChecklist: (fromIndex, toIndex) =>
		set((state) => {
			const items = [...state.checklist]
			const [removed] = items.splice(fromIndex, 1)
			items.splice(toIndex, 0, removed)
			return {
				checklist: items.map((item, index) => ({
					...item,
					sort_order: index,
				})),
			}
		}),

	setIsGenerating: (isGenerating) => set({ isGenerating }),

	setGenerationError: (generationError) => set({ generationError }),

	// Created project
	setCreatedProject: (createdProject) => set({ createdProject }),

	// Overall state
	setIsSubmitting: (isSubmitting) => set({ isSubmitting }),

	setError: (error) => set({ error }),

	// Validation helpers
	isStep1Valid: () => {
		const { basicDetails } = get()
		return (
			basicDetails.name.trim().length >= 3 &&
			basicDetails.certificates.length > 0
		)
	},

	isStep2Valid: () => {
		const { projectScope } = get()
		const hasIndustryOrDomain = projectScope.industry.trim().length > 0 || projectScope.domain.trim().length > 0
		const hasAssociatedRoles = (projectScope.associatedRoles || []).length > 0
		return hasIndustryOrDomain && hasAssociatedRoles
	},

	isStep3Valid: () => {
		// Documents are optional
		return true
	},

	isStep4Valid: () => {
		// Review step - validate all previous steps
		const { isStep1Valid, isStep2Valid, isStep3Valid } = get()
		return isStep1Valid() && isStep2Valid() && isStep3Valid()
	},

	isCurrentStepValid: () => {
		const { currentStep, isStep1Valid, isStep2Valid, isStep3Valid, isStep4Valid } = get()
		switch (currentStep) {
			case 1:
				return isStep1Valid()
			case 2:
				return isStep2Valid()
			case 3:
				return isStep3Valid()
			case 4:
				return isStep4Valid()
			case 5:
				return true
			default:
				return false
		}
	},

	// Get all wizard data for submission
	getWizardData: () => {
		const { basicDetails, projectScope, documents, checklist } = get()
		return {
			basicDetails,
			projectScope,
			documents,
			checklist,
		}
	},

	// Retry AI checklist generation
	retryGeneration: async () => {
		const { createdProject } = get()

		if (!createdProject || !createdProject.id) {
			set({ generationError: 'No project found. Please create the project first.' })
			return
		}

		set({ isGenerating: true, generationError: null, checklist: [] })

		try {
			const checklistResponse = await projectService.generateChecklist(createdProject.id)
			// Ensure checklist data is always an array
			const checklistData = checklistResponse?.data
			const checklistArray = Array.isArray(checklistData) ? checklistData : []
			set({ checklist: checklistArray, isGenerating: false })
		} catch (error) {
			console.error('Checklist generation retry failed:', error)
			set({
				generationError: error.message || 'Failed to generate checklist. Please try again.',
				isGenerating: false,
			})
		}
	},

	// Reset wizard to initial state
	reset: () => set({ ...initialState }),

	// Reset only specific sections
	resetBasicDetails: () => set({ basicDetails: { ...initialBasicDetails } }),
	resetProjectScope: () => set({ projectScope: { ...initialProjectScope } }),
	resetDocuments: () => set({ documents: [], uploadProgress: {} }),
	resetChecklist: () =>
		set({ checklist: [], isGenerating: false, generationError: null }),
}))

export default useProjectWizardStore
